import { ILayoutElementData } from "@faire/web-api/indigofair/data/ILayoutElementData";
import { ISimpleImageTitle } from "@faire/web-api/indigofair/data/ISimpleImageTitle";
import { isNotUndefined } from "@faire/web/common/typescriptUtils";
import { isResponsiveProp } from "@faire/web/slate/Theme/ResponsiveProp";
import { ResponsiveContentV2 } from "@faire/web/ui/ResponsiveContentV2";
import { observer } from "mobx-react";
import React from "react";

import { PosterTile } from "@faire/retailer/components/Carousel/PosterTile";
import { GridViewBase } from "@faire/retailer/components/LayoutElementRenderer/__internal__/Carousel/ByType/GridViewBase";
import {
  BaseCarouselProps,
  CarouselBase,
  IProps,
} from "@faire/retailer/components/LayoutElementRenderer/__internal__/Carousel/CarouselBase";
import { Context } from "@faire/retailer/components/LayoutElementRenderer/__internal__/Context";

const DEFAULT_TABLET_COLUMNS = 3;
const DEFAULT_DESKTOP_COLUMNS = 4;
const DEFAULT_ROWS = 2;

const AttributePosterCarouselBase: React.FC<BaseCarouselProps> = (props) => {
  const {
    layoutElement,
    requestId,
    onItemClick,
    onVisible,
    useLazyImageLoading,
  } = React.useContext(Context);
  const { on_click_event_name } = layoutElement;

  const isResponsiveItemsPerSlide = isResponsiveProp(props.itemsPerSlide);
  const desktopItemsPerSlide = ((isResponsiveItemsPerSlide &&
  typeof props.itemsPerSlide !== "number"
    ? props.itemsPerSlide!.desktopAndAbove
    : props.itemsPerSlide) ?? DEFAULT_DESKTOP_COLUMNS) as number;
  const tabletItemsPerSlide = ((isResponsiveItemsPerSlide &&
  typeof props.itemsPerSlide !== "number"
    ? props.itemsPerSlide!.tabletAndAbove
    : props.itemsPerSlide) ?? DEFAULT_TABLET_COLUMNS) as number;

  const desktopLayoutElementData = layoutElement.layout_element_data.slice(
    0,
    desktopItemsPerSlide * DEFAULT_ROWS
  );

  const tabletLayoutElementData = filterArrayByEveryNthElement(
    layoutElement.layout_element_data,
    desktopItemsPerSlide
  ).slice(0, tabletItemsPerSlide * DEFAULT_ROWS);

  const getItems = (data: ILayoutElementData[]) =>
    data
      .map((layoutElementData) => layoutElementData["simple_image_title"])
      .filter(isNotUndefined);

  const renderPosterTile = (posterTile: ISimpleImageTitle) => {
    const { event_tracking_token, cta_link, main_text, image } = posterTile;
    if (
      event_tracking_token === undefined ||
      cta_link === undefined ||
      main_text === undefined
    ) {
      return null;
    }
    return (
      <PosterTile
        key={event_tracking_token}
        token={event_tracking_token}
        link={cta_link}
        text={main_text}
        image={image}
        onClickEventName={on_click_event_name}
        tileOnClick={(token) => {
          onItemClick?.(
            on_click_event_name ?? "",
            requestId ?? "",
            token ?? ""
          );
        }}
        useLazyImageLoading={useLazyImageLoading}
      />
    );
  };

  const CarouselBaseComponent = CarouselBase as React.ComponentType<
    IProps<ISimpleImageTitle>
  >;

  const onItemsVisible = (items: ISimpleImageTitle[]) => {
    const itemTokens = items
      .map((item) => item.event_tracking_token)
      .filter(isNotUndefined);
    if (layoutElement.on_click_event_name) {
      onVisible?.(
        layoutElement.on_click_event_name,
        requestId,
        itemTokens?.join(" ") ?? ""
      );
    }
  };

  return (
    <div className={props.className}>
      <ResponsiveContentV2
        mobile={
          <CarouselBaseComponent
            items={getItems(layoutElement.layout_element_data)}
            renderItem={renderPosterTile}
            itemsPerSlide={2}
            itemGap={0}
            onItemsVisible={onItemsVisible}
            peekNext
          />
        }
        tablet={
          <GridViewBase
            items={getItems(tabletLayoutElementData)}
            renderItem={renderPosterTile}
            rows={DEFAULT_ROWS}
            itemsPerSlide={3}
            expandable={false}
            gap={0}
          />
        }
        desktopAndAbove={
          <GridViewBase
            items={getItems(desktopLayoutElementData)}
            renderItem={renderPosterTile}
            rows={DEFAULT_ROWS}
            itemsPerSlide={4}
            expandable={false}
            gap={0}
          />
        }
      />
    </div>
  );
};

export const AttributePosterCarousel = observer(AttributePosterCarouselBase);

const filterArrayByEveryNthElement = <T,>(arr: T[], n: number) =>
  arr.filter((_, index) => (index + 1) % n !== 0);
